import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  CheckCircle,
  XCircle,
  Home,
  Gift,
  RefreshCw,
  User,
  Copy,
  Check,
} from "lucide-react";
import { resolvePosnetError } from "../Utils/posnetErrors";
import UygulamayaGec from "../Components/UygulamayaGec";

const PaymentResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);
  const [giftCodes, setGiftCodes] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  const success = searchParams.get("success") === "true";
  const amount = searchParams.get("amount");
  const respText = searchParams.get("respText") || searchParams.get("error");
  const authCode = searchParams.get("authCode");
  const xid = searchParams.get("xid");
  const respCode = searchParams.get("respCode") || searchParams.get("errorCode");

  // 🔴 URL'den al (localStorage yerine!)
  const paymentType = searchParams.get("type") || "REGISTER";

  // 🔴 Resolved Error Message
  // Banka onay kodu verdiyse para çekilmiştir: işlem tamamlanamadıysa kullanıcı TEKRAR ÖDEME YAPMAMALI
  const paraCekildiIslemTamamlanmadi = !success && !!authCode;
  // Yenileme ön kontrolü bankaya gidilmeden reddettiyse ücret çekilmedi; sebep sunucudan gelen metin
  const yenilemeOnKontroluReddetti = !success && paymentType === "RENEWAL" && !authCode && !respCode && !!respText;

  const resolvedError = success
    ? null
    : paraCekildiIslemTamamlanmadi
    ? {
        title: "Ödeme alındı, işlem tamamlanamadı",
        description:
          "Kartınızdan ödeme alındı ancak işlem tamamlanamadı. Lütfen tekrar ödeme yapmayın; destek ekibimizle iletişime geçin, işleminiz elle tamamlanacak.",
      }
    : yenilemeOnKontroluReddetti
    ? { title: "Ödeme alınmadı", description: `${respText} Kartınızdan ücret çekilmedi.` }
    : resolvePosnetError(respCode, respText);

  // ✅ Hediye kodlarını çek
  useEffect(() => {
    const fetchGiftCodes = async () => {
      if (success && paymentType === "GIFT_CODE" && xid) {
        console.log("🎁 Hediye kodları çekiliyor...", xid);
        setLoading(true);

        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/posnet/hediye-kodlari/${xid}`,
          );

          console.log("📡 Response status:", response.status);

          const data = await response.json();
          console.log("📦 Gelen data:", data);

          if (data.success && data.codes) {
            console.log("✅ Kodlar alındı:", data.codes);
            setGiftCodes(data.codes);
          }
        } catch (err) {
          console.error("❌ Hediye kodları hatası:", err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchGiftCodes();
  }, [success, paymentType, xid]);

  // 🔴 Countdown ve yönlendirme - GIFT_CODE ve REGISTER hariç!
  // REGISTER: yeni üye web'e değil uygulamaya girecek (telefon + kayıtta
  // belirlediği şifre). Bu yüzden burada yönlendirme yok, yönerge var.
  useEffect(() => {
    if (success && paymentType !== "GIFT_CODE" && paymentType !== "REGISTER") {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);

            if (paymentType === "RENEWAL") {
              navigate("/yenile");
            } else {
              navigate("/");
            }

            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [success, navigate, paymentType]);

  const handleCopy = (code, index) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const getSuccessMessage = () => {
    if (paymentType === "REGISTER") {
      return {
        title: "Üyeliğiniz hazır! 🎉",
        subtitle: "Ödemeniz alındı, hesabınız oluşturuldu.",
        description:
          "UniversalTeam uygulamasını indirip telefon numaranız ve şifrenizle giriş yapabilirsiniz. " +
          "Kalan profil bilgilerinizi uygulama içinde tamamlayacaksınız.",
        buttonText: "Ana Sayfaya Dön",
        buttonIcon: <Home size={18} />,
        redirectPath: "/",
      };
    } else if (paymentType === "GIFT_CODE") {
      return {
        title: "Hediye Kodu Satın Alma Başarılı! 🎁",
        subtitle: "Hediye kodlarınız oluşturuldu.",
        description:
          giftCodes.length > 0
            ? `${giftCodes.length} adet hediye kodunuz hazır!`
            : "Kodlarınız hazırlanıyor...",
        buttonText: "Ana Sayfaya Dön",
        buttonIcon: <Home size={18} />,
        redirectPath: "/",
      };
    } else if (paymentType === "RENEWAL") {
      return {
        title: "Abonelik Yenileme Başarılı! ✅",
        subtitle:
          searchParams.get("message") === "already_processed"
            ? "Bu ödeme daha önce işlendi; tekrar ücret alınmadı."
            : "Aboneliğiniz 1 yıl uzatıldı.",
        description: "Yeni bitiş tarihinizi üyelik sayfanızda görebilirsiniz.",
        buttonText: "Üyelik Bilgilerim",
        buttonIcon: <User size={18} />,
        redirectPath: "/yenile",
      };
    }

    return {
      title: "Ödeme Başarılı! ✅",
      subtitle: "İşleminiz tamamlandı.",
      description: "",
      buttonText: "Ana Sayfa",
      buttonIcon: <Home size={18} />,
      redirectPath: "/",
    };
  };

  const successContent = getSuccessMessage();

  return (
    <div
      style={{
        minHeight: "100vh",
        background: success
          ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
          : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes scaleIn {
            from { transform: scale(0); }
            to { transform: scale(1); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      <div
        style={{
          background: "white",
          borderRadius: "24px",
          padding: "clamp(30px, 5vw, 50px)",
          maxWidth: "550px",
          width: "100%",
          textAlign: "center",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
          animation: "fadeIn 0.5s ease-out",
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: "100px",
            height: "100px",
            background: success
              ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
              : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 25px",
            color: "white",
            boxShadow: success
              ? "0 12px 35px rgba(16, 185, 129, 0.35)"
              : "0 12px 35px rgba(239, 68, 68, 0.35)",
            animation: "scaleIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
          }}
        >
          {success ? <CheckCircle size={50} /> : <XCircle size={50} />}
        </div>

        {!success && (
          <span
            style={{
              color: "#ef4444",
              fontSize: "13px",
              fontWeight: "700",
              letterSpacing: "1.5px",
              display: "block",
              marginBottom: "8px",
              textTransform: "uppercase",
            }}
          >
            Ödeme Başarısız
          </span>
        )}

        {/* Title */}
        <h2
          style={{
            fontSize: success
              ? "clamp(22px, 5vw, 28px)"
              : "clamp(28px, 7vw, 42px)", // 🔴 Fail durumunda çok daha büyük
            fontWeight: "800",
            marginBottom: "15px",
            color: success ? "#1e293b" : "#ef4444", // 🔴 Fail durumunda kırmızı
            lineHeight: "1.2",
            textTransform: "uppercase",
          }}
        >
          {success ? successContent.title : (resolvedError?.title || "HATA")}
        </h2>

        <p
          style={{
            fontSize: "clamp(15px, 3.5vw, 18px)",
            color: success ? "#64748b" : "#475569",
            marginBottom: "30px",
            fontWeight: "600",
            lineHeight: "1.5",
          }}
        >
          {success ? successContent.subtitle : ""}
        </p>

        {/* Description */}
        {success && successContent.description && (
          <p
            style={{
              fontSize: "clamp(13px, 2.5vw, 14px)",
              color: "#64748b",
              marginBottom: "25px",
            }}
          >
            {successContent.description}
          </p>
        )}

        {/* Kayıt: üye web'e değil uygulamaya telefonla girer; cihaza göre uygulamayı aç / mağaza.
            Açıklamanın hemen altında: telefonda kaydırmadan görünsün (işlem detayları sonra). */}
        {success && paymentType === "REGISTER" && (
          <div style={{ marginBottom: "25px" }}>
            <UygulamayaGec />
          </div>
        )}

        {/* 🎁 HEDİYE KODLARI BÖLÜMÜ */}
        {success && paymentType === "GIFT_CODE" && (
          <div
            style={{
              padding: "20px",
              background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
              border: "2px solid #f59e0b",
              borderRadius: "16px",
              marginBottom: "25px",
              textAlign: "left",
            }}
          >
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#92400e",
                marginBottom: "15px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Gift size={20} />
              Hediye Kodlarınız
            </h3>

            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: "#92400e",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "4px solid #fde68a",
                    borderTop: "4px solid #f59e0b",
                    borderRadius: "50%",
                    margin: "0 auto 10px",
                    animation: "spin 1s linear infinite",
                  }}
                />
                <p style={{ margin: 0, fontSize: "14px" }}>
                  Kodlar yükleniyor...
                </p>
              </div>
            ) : giftCodes.length > 0 ? (
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {giftCodes.map((code, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: "white",
                        padding: "12px 16px",
                        borderRadius: "12px",
                        border: "1px solid #f59e0b",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontSize: "15px",
                          fontWeight: "600",
                          color: "#92400e",
                          letterSpacing: "1px",
                        }}
                      >
                        {code}
                      </span>
                      <button
                        onClick={() => handleCopy(code, index)}
                        style={{
                          background:
                            copiedIndex === index ? "#10b981" : "#f59e0b",
                          border: "none",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          color: "white",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "13px",
                          fontWeight: "600",
                          transition: "all 0.2s",
                        }}
                      >
                        {copiedIndex === index ? (
                          <>
                            <Check size={14} />
                            Kopyalandı
                          </>
                        ) : (
                          <>
                            <Copy size={14} />
                            Kopyala
                          </>
                        )}
                      </button>
                    </div>
                  ))}
                </div>

                <p
                  style={{
                    marginTop: "12px",
                    fontSize: "12px",
                    color: "#92400e",
                    opacity: 0.8,
                  }}
                >
                  💡 Bu kodları kaydetmeyi unutmayın! SMS ve email ile de
                  gönderildi.
                </p>
              </>
            ) : (
              <p style={{ color: "#92400e", fontSize: "14px", margin: 0 }}>
                Kodlar SMS ve email ile gönderildi.
              </p>
            )}
          </div>
        )}

        {/* Transaction Details */}
        {success && amount && (
          <div
            style={{
              padding: "20px",
              background: "#f0fdf4",
              border: "1px solid #10b981",
              borderRadius: "12px",
              marginBottom: "25px",
              textAlign: "left",
            }}
          >
            <h3
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#065f46",
                marginBottom: "12px",
              }}
            >
              İşlem Detayları
            </h3>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#64748b", fontSize: "13px" }}>
                  Tutar:
                </span>
                <span
                  style={{
                    color: "#065f46",
                    fontWeight: "600",
                    fontSize: "13px",
                  }}
                >
                  {(parseInt(amount) / 100).toFixed(2)}₺
                </span>
              </div>

              {authCode && (
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span style={{ color: "#64748b", fontSize: "13px" }}>
                    Onay Kodu:
                  </span>
                  <span
                    style={{
                      color: "#065f46",
                      fontWeight: "500",
                      fontSize: "13px",
                    }}
                  >
                    {authCode}
                  </span>
                </div>
              )}

              {xid && (
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span style={{ color: "#64748b", fontSize: "13px" }}>
                    İşlem No:
                  </span>
                  <span
                    style={{
                      color: "#065f46",
                      fontWeight: "500",
                      fontSize: "11px",
                      fontFamily: "monospace",
                    }}
                  >
                    {xid.substring(0, 10)}...
                  </span>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#64748b", fontSize: "13px" }}>
                  Tarih:
                </span>
                <span
                  style={{
                    color: "#065f46",
                    fontWeight: "500",
                    fontSize: "13px",
                  }}
                >
                  {new Date().toLocaleDateString("tr-TR", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Error Details */}
        {!success && (
          <div
            style={{
              padding: "15px",
              background: "#fef2f2",
              border: "1px solid #ef4444",
              borderRadius: "12px",
              marginBottom: "25px",
              textAlign: "left",
            }}
          >
            <p
              style={{
                color: "#991b1b",
                fontSize: "14px",
                margin: 0,
                lineHeight: "1.6",
              }}
            >
              {resolvedError.description}
            </p>
            {respCode && (
              <p
                style={{
                  color: "#991b1b",
                  fontSize: "12px",
                  margin: "8px 0 0 0",
                  opacity: "0.8",
                }}
              >
                Hata Kodu: {respCode}
              </p>
            )}
          </div>
        )}

        {/* Countdown - 🔴 GIFT_CODE ve REGISTER hariç (yukarıdaki yönlendirmeyle aynı koşul) */}
        {success && paymentType !== "GIFT_CODE" && paymentType !== "REGISTER" && (
          <div
            style={{
              padding: "15px",
              background: "#fef3c7",
              border: "1px solid #f59e0b",
              borderRadius: "12px",
              marginBottom: "25px",
              fontSize: "clamp(12px, 3vw, 14px)",
              color: "#92400e",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <CheckCircle size={16} />
            <span>{countdown} saniye içinde yönlendirileceksiniz...</span>
          </div>
        )}

        {/* Buttons */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {success ? (
            <>
              {paymentType !== "REGISTER" && (
              <button
                onClick={() => navigate(successContent.redirectPath)}
                style={{
                  flex: 1,
                  minWidth: "150px",
                  padding: "14px 25px",
                  background:
                    "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  border: "none",
                  borderRadius: "12px",
                  color: "white",
                  fontSize: "clamp(13px, 3vw, 15px)",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "center",
                  boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
                }}
              >
                {successContent.buttonIcon}
                {successContent.buttonText}
              </button>
              )}

              <button
                onClick={() => navigate("/")}
                style={{
                  padding: "14px 25px",
                  background: "white",
                  border: "2px solid #e2e8f0",
                  borderRadius: "12px",
                  color: "#64748b",
                  fontSize: "clamp(13px, 3vw, 15px)",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "center",
                }}
              >
                <Home size={18} />
                Ana Sayfa
              </button>
            </>
          ) : (
            <>
              {!paraCekildiIslemTamamlanmadi && (
              <button
                onClick={() => (paymentType === "RENEWAL" ? navigate("/yenile") : navigate(-1))}
                style={{
                  flex: 1,
                  minWidth: "150px",
                  padding: "14px 25px",
                  background:
                    "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  border: "none",
                  borderRadius: "12px",
                  color: "white",
                  fontSize: "clamp(13px, 3vw, 15px)",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "center",
                  boxShadow: "0 4px 15px rgba(239, 68, 68, 0.3)",
                }}
              >
                <RefreshCw size={18} />
                Tekrar Dene
              </button>
              )}

              <button
                onClick={() => navigate("/")}
                style={{
                  padding: "14px 25px",
                  background: "white",
                  border: "2px solid #e2e8f0",
                  borderRadius: "12px",
                  color: "#64748b",
                  fontSize: "clamp(13px, 3vw, 15px)",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "center",
                }}
              >
                <Home size={18} />
                Ana Sayfa
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: "25px",
            paddingTop: "20px",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <p
            style={{
              fontSize: "clamp(11px, 2.5vw, 12px)",
              color: "#94a3b8",
              margin: 0,
            }}
          >
            {success
              ? "İşleminiz başarıyla tamamlandı. Teşekkür ederiz."
              : "Sorun devam ederse lütfen destek ekibimizle iletişime geçin."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentResult;
